import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import {
  askSatisfied,
  finalScore,
  followUpNudge,
  followUpStrongHint,
  FULL_LINE,
  HALFWAY_LINES,
  makeSession,
  nudgeLine,
  PANTRY,
  PERFECT_LINE,
  pickIntroLine,
  PRAISE_LINES,
  roundSatisfies,
  ROUNDS_PER_MEAL,
  strongHint,
  STREAK_LINES,
  STREAK_MILESTONES,
  totalSnacks,
  trayLevelAfterMeal,
} from "../components/games/feed-monster/gameUtils";
import { useMunchySoundManager } from "../components/games/feed-monster/SoundManager";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import BackToGamesButton from "../components/games/BackToGamesButton";
import StarRewardBadge from "../components/games/StarRewardBadge";
import { SpeakerIcon, StarIcon, HeartIcon, PlateIcon } from "../components/games/ui-icons";
import "./FeedMonsterPage.css";

const F = { fontFamily: "'Fredoka', sans-serif" };
const TRAY_LEVEL_KEY = "fm_tray_level";
const MET_MUNCHY_KEY = "fm_met_munchy";

const MONSTER_POSES = {
  idle: "/assets/games/feed-monster/fm_monster_idle.png",
  chomp: "/assets/games/feed-monster/fm_monster_chomp.png",
  happy: "/assets/games/feed-monster/fm_monster_happy.png",
};

const CONFETTI_BITS = Array.from({ length: 26 }, (_, index) => ({
  id: index,
  left: `${4 + ((index * 31) % 92)}%`,
  delay: `${(index % 7) * 0.09}s`,
  color: ["#FF8A3D", "#FFC729", "#40B866", "#408CF2", "#FF8CBF", "#9973E6"][index % 6],
}));

const SPARKLES = Array.from({ length: 9 }, (_, index) => {
  const angle = (index / 9) * Math.PI * 2;
  return { id: index, dx: Math.cos(angle) * 78, dy: Math.sin(angle) * 78 };
});

const HEARTS = [
  { id: 0, x: -34, delay: 0 },
  { id: 1, x: 4, delay: 0.14 },
  { id: 2, x: 38, delay: 0.28 },
];

const readTrayLevel = () => {
  try {
    const raw = Number(localStorage.getItem(TRAY_LEVEL_KEY));
    return Number.isFinite(raw) ? Math.min(2, Math.max(0, raw)) : 0;
  } catch {
    return 0;
  }
};

const StartModal = ({ onStart }) => (
  <div className="fm-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="fm-start-title">
    <div className="fm-modal" style={F}>
      <motion.img
        className="fm-modal-monster"
        src={MONSTER_POSES.idle}
        alt="Munchy the monster"
        animate={{ y: [-6, 6] }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
        draggable="false"
      />
      <h2 id="fm-start-title">Munchy is hungry!</h2>
      <p>Munchy asks for a snack every round. Drag the right one into his big open mouth!</p>
      <div className="fm-how-list">
        <div className="fm-how-step"><span className="fm-how-number">1</span><span>Listen to what Munchy wants.</span></div>
        <div className="fm-how-step"><span className="fm-how-number">2</span><span>Drag that snack up into his mouth.</span></div>
        <div className="fm-how-step"><span className="fm-how-number">3</span><span>Feed him every snack he asks for to fill his tummy!</span></div>
      </div>
      <button type="button" className="fm-primary-button" onClick={onStart}>Let&apos;s feed Munchy!</button>
    </div>
  </div>
);

const TummyMeter = ({ fed, total }) => {
  const fraction = total === 0 ? 0 : (fed / total) * 100;
  return (
    <div className="fm-tummy" role="progressbar" aria-label="Munchy's tummy" aria-valuemin="0" aria-valuemax={total} aria-valuenow={fed}>
      <span className="fm-tummy-icon" aria-hidden="true"><PlateIcon size={16} /></span>
      <div className="fm-tummy-track">
        <motion.div
          className="fm-tummy-fill"
          initial={false}
          animate={{ width: `${fraction}%` }}
          transition={{ type: "spring", stiffness: 120, damping: 18 }}
        >
          <span className="fm-tummy-shine" aria-hidden="true" />
        </motion.div>
      </div>
      <span className="fm-tummy-count">{fed}/{total}</span>
    </div>
  );
};

const FeedMonsterPage = () => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { refreshUserData } = useAuth();
  const sounds = useMunchySoundManager(apiClient);

  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState("intro"); // intro | playing | results
  const [rounds, setRounds] = useState([]);
  const [roundIndex, setRoundIndex] = useState(0);
  const [fedCount, setFedCount] = useState(0);
  const [mood, setMood] = useState("idle");
  const [dragging, setDragging] = useState(null);
  const [mouthHot, setMouthHot] = useState(false);
  const [flying, setFlying] = useState(null);
  const [sparkleKey, setSparkleKey] = useState(0);
  const [sparklePoint, setSparklePoint] = useState({ x: 0, y: 0 });
  const [flashText, setFlashText] = useState(null);
  const [resultFirstTry, setResultFirstTry] = useState(0);
  const [nudgeReset, setNudgeReset] = useState(0);
  // Two-step rounds: true while Munchy is waiting for the follow-up snack.
  const [inFollowUp, setInFollowUp] = useState(false);
  // Misses on the current ask — the 2nd earns a stronger hint + a glowing answer.
  const [missCount, setMissCount] = useState(0);
  const [starsAwarded, setStarsAwarded] = useState(0);
  const [resultSnacks, setResultSnacks] = useState(0);
  // Perfect meal (every round first-try) earns a special title + a 2nd wave.
  const [perfectWave, setPerfectWave] = useState(false);
  // First meeting = the canonical "I'm Munchy!" hello; afterwards a random
  // greeting keeps openings fresh (mirrors iOS `fm_met_munchy`).
  const [introLine] = useState(() => {
    let hasMet = true;
    try {
      hasMet = localStorage.getItem(MET_MUNCHY_KEY) === "true";
      localStorage.setItem(MET_MUNCHY_KEY, "true");
    } catch {
      // Storage optional — default to the fresh (random) greeting.
    }
    return pickIntroLine(hasMet);
  });

  const monsterShake = useAnimationControls();
  const trayControls = useAnimationControls();

  const monsterRef = useRef(null);
  const fieldRef = useRef(null);
  const busyRef = useRef(false);
  const draggingRef = useRef(null);
  const roundHadMissRef = useRef(false);
  const streakRef = useRef(0);
  const roundIndexRef = useRef(0);
  const firstTryRef = useRef(0);
  const fedItemsRef = useRef([]);
  const trayLevelRef = useRef(readTrayLevel());
  const timersRef = useRef([]);
  const inFollowUpRef = useRef(false);
  const missCountRef = useRef(0);

  const currentRound = rounds[roundIndex] || null;
  const mealSnacks = totalSnacks(rounds);
  // What Munchy is asking for right now (part one or the follow-up).
  const currentAsk = currentRound
    ? inFollowUp
      ? currentRound.followUpPrompt || currentRound.prompt
      : currentRound.prompt
    : null;
  const currentSwatch = currentRound && !inFollowUp ? currentRound.swatch : null;
  // During a follow-up ask the already-eaten part-one snack is gone from the tray.
  const trayOptions = currentRound
    ? inFollowUp
      ? (currentRound.options || []).filter((item) => item.id !== currentRound.target.id)
      : currentRound.options || []
    : [];

  const setFollowUp = useCallback((value) => {
    inFollowUpRef.current = value;
    setInFollowUp(value);
  }, []);
  const setMisses = useCallback((value) => {
    missCountRef.current = value;
    setMissCount(value);
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = window.setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  useEffect(() => () => {
    clearTimers();
    sounds.stop();
  }, [clearTimers, sounds]);

  // Warm Munchy's greeting as early as we can (and again if the API client
  // finishes loading) so the clip is already cached the instant the child taps
  // "Let's feed Munchy!" — the greeting then plays in his real voice inside that
  // tap, instead of the robotic synth understudy that a cold cache would force.
  useEffect(() => {
    sounds.warm(introLine);
  }, [sounds, introLine, apiClient]);

  // Idle nudge: after ~9s of no interaction, wiggle the tray and re-ask.
  useEffect(() => {
    if (phase !== "playing") return undefined;
    const id = window.setTimeout(() => {
      if (busyRef.current) return;
      trayControls.start({ x: [0, -7, 7, -5, 5, 0], transition: { duration: 0.55 } });
      const round = rounds[roundIndex];
      if (round) {
        sounds.speak(inFollowUpRef.current ? round.followUpPrompt || round.prompt : round.prompt);
      }
    }, 9000);
    return () => window.clearTimeout(id);
  }, [phase, roundIndex, nudgeReset, rounds, trayControls, sounds]);

  const bumpInteraction = useCallback(() => setNudgeReset((value) => value + 1), []);

  const mouthCenter = useCallback(() => {
    const rect = monsterRef.current?.getBoundingClientRect();
    if (!rect) return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    return { x: rect.left + rect.width * 0.5, y: rect.top + rect.height * 0.6 };
  }, []);

  // Forgiving drop test: anywhere on Munchy's body counts; a long upward drag
  // is handled by the caller. Never lets the interaction silently dead-end.
  const isNearMouth = useCallback((x, y) => {
    const rect = monsterRef.current?.getBoundingClientRect();
    if (rect) {
      const mx = rect.left + rect.width * 0.5;
      const my = rect.top + rect.height * 0.6;
      if (Math.hypot(x - mx, y - my) < rect.height * 0.62) return true;
    }
    const field = fieldRef.current?.getBoundingClientRect();
    if (field) return y - field.top < field.height * 0.6;
    return false;
  }, []);

  const persistProgress = useCallback(
    async (score) => {
      if (!apiClient) return;
      try {
        const response = await apiClient.postGameProgress("FEED_MONSTER", score, true, null);
        setStarsAwarded(Number(response?.stars_awarded || 0));
        window.dispatchEvent(new CustomEvent("games:progress-updated"));
        refreshUserData?.();
      } catch {
        // The celebration is already on screen — saving must never block play.
      }
    },
    [apiClient, refreshUserData]
  );

  // Pre-generate this round's possible "wrong snack" hint lines (gentle + strong)
  // plus any follow-up prompt/hints, so Munchy's real voice answers even the very
  // first mistake.
  const warmRoundHints = useCallback(
    (round) => {
      if (!round) return;
      const lines = [];
      (round.options || []).forEach((item) => {
        if (!roundSatisfies(round, item)) lines.push(nudgeLine(round, item));
      });
      lines.push(strongHint(round));
      if (round.followUp) {
        if (round.followUpPrompt) lines.push(round.followUpPrompt);
        (round.options || []).forEach((item) => {
          if (item.id !== round.followUp.id && item.id !== round.target.id) {
            lines.push(followUpNudge(round, item));
          }
        });
        lines.push(followUpStrongHint(round));
      }
      sounds.warm(lines);
    },
    [sounds]
  );

  const finishMeal = useCallback(() => {
    busyRef.current = true;
    setMood("happy");
    const fedItems = fedItemsRef.current;
    const tries = firstTryRef.current;
    const roundCount = rounds.length || ROUNDS_PER_MEAL;
    const isPerfect = tries >= roundCount;
    trayLevelRef.current = trayLevelAfterMeal(trayLevelRef.current, tries, roundCount);
    try {
      localStorage.setItem(TRAY_LEVEL_KEY, String(trayLevelRef.current));
    } catch {
      // Storage is optional.
    }
    setResultFirstTry(tries);
    setResultSnacks(fedItems.length);
    setPhase("results");
    sounds.playCelebrate();
    if (isPerfect) {
      // The biggest cheer Munchy has, plus a second confetti wave.
      sounds.speak(PERFECT_LINE);
      schedule(() => {
        setPerfectWave(true);
        sounds.playCelebrate();
      }, 800);
    } else {
      sounds.speak(FULL_LINE);
    }
    persistProgress(finalScore(tries, roundCount));
  }, [persistProgress, sounds, rounds, schedule]);

  const advance = useCallback(() => {
    roundHadMissRef.current = false;
    setMisses(0);
    setFollowUp(false);
    const next = roundIndexRef.current + 1;
    if (next >= rounds.length) {
      finishMeal();
      return;
    }
    roundIndexRef.current = next;
    setRoundIndex(next);
    setMood("idle");
    busyRef.current = false;
    const round = rounds[next];
    if (round) {
      warmRoundHints(round);
      schedule(() => sounds.speak(round.prompt), 350);
    }
    bumpInteraction();
  }, [rounds, finishMeal, schedule, sounds, bumpInteraction, setMisses, setFollowUp, warmRoundHints]);

  const swallow = useCallback(
    (item) => {
      setFlying(null);
      setMood("chomp");
      fedItemsRef.current.push(item);
      const fedNow = fedItemsRef.current.length;
      sounds.playChomp(mealSnacks ? fedNow / mealSnacks : 0.5);
      setFedCount((value) => value + 1);

      const mouth = mouthCenter();
      setSparklePoint(mouth);
      setSparkleKey((key) => key + 1);

      const round = rounds[roundIndexRef.current];

      // Two-step rounds: part one down — Munchy immediately wants part two. The
      // eaten part-one snack (the round target) is dropped from the tray.
      if (!inFollowUpRef.current && round && round.followUp) {
        schedule(() => setMood("idle"), 550);
        schedule(() => {
          setMisses(0);
          setFollowUp(true);
          busyRef.current = false;
          sounds.speak(round.followUpPrompt || "And one more, please!");
          bumpInteraction();
        }, 900);
        return;
      }

      // Round complete — scoring + celebration.
      const missed = roundHadMissRef.current;
      if (!missed) {
        streakRef.current += 1;
        firstTryRef.current += 1;
      } else {
        streakRef.current = 0;
      }

      const completedRounds = roundIndexRef.current + 1;
      const roundCount = rounds.length || ROUNDS_PER_MEAL;
      if (!missed && STREAK_MILESTONES.includes(streakRef.current)) {
        // Streak milestones trade the regular praise for a celebration.
        setFlashText(`${streakRef.current} IN A ROW!`);
        sounds.playCelebrate();
        sounds.speak(STREAK_LINES[Math.floor(Math.random() * STREAK_LINES.length)]);
        schedule(() => setFlashText(null), 1700);
      } else if (completedRounds === Math.floor(roundCount / 2) && completedRounds < roundCount) {
        // The halfway-tummy beat.
        sounds.speak(HALFWAY_LINES[Math.floor(Math.random() * HALFWAY_LINES.length)]);
      } else {
        sounds.speak(PRAISE_LINES[Math.floor(Math.random() * PRAISE_LINES.length)]);
      }

      schedule(() => setMood("happy"), 550);
      schedule(() => advance(), 1500);
    },
    [advance, mouthCenter, schedule, sounds, rounds, mealSnacks, setMisses, setFollowUp, bumpInteraction]
  );

  const attemptFeed = useCallback(
    (item, point) => {
      const round = rounds[roundIndexRef.current];
      if (!round) return;

      if (askSatisfied(round, item, inFollowUpRef.current)) {
        busyRef.current = true;
        setFlying({ item, from: point, to: mouthCenter() });
      } else {
        // Not what Munchy asked for — head shake + a spoken hint. The first miss
        // gets a gentle redirect; from the second on, the hint gets descriptive
        // and the right answer starts glowing.
        roundHadMissRef.current = true;
        const nextMiss = missCountRef.current + 1;
        setMisses(nextMiss);
        monsterShake.start({ x: [0, -13, 13, -10, 10, -5, 5, 0], transition: { duration: 0.5 } });
        sounds.playWrong();
        let line;
        if (nextMiss >= 2) {
          line = inFollowUpRef.current ? followUpStrongHint(round) : strongHint(round);
        } else {
          line = inFollowUpRef.current ? followUpNudge(round, item) : nudgeLine(round, item);
        }
        sounds.speak(line);
      }
    },
    [rounds, mouthCenter, monsterShake, sounds, setMisses]
  );

  const handlePointerDown = useCallback(
    (item, event) => {
      if (busyRef.current || draggingRef.current || phase !== "playing") return;
      event.preventDefault();
      bumpInteraction();

      const start = { x: event.clientX, y: event.clientY };
      draggingRef.current = { item, startX: start.x, startY: start.y, moved: false };
      setDragging({ item, x: start.x, y: start.y });
      setMouthHot(false);
      sounds.playPop();

      const onMove = (moveEvent) => {
        const drag = draggingRef.current;
        if (!drag) return;
        if (Math.hypot(moveEvent.clientX - drag.startX, moveEvent.clientY - drag.startY) > 6) {
          drag.moved = true;
        }
        setDragging({ item, x: moveEvent.clientX, y: moveEvent.clientY });
        setMouthHot(isNearMouth(moveEvent.clientX, moveEvent.clientY));
      };

      const onUp = (upEvent) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        const drag = draggingRef.current;
        draggingRef.current = null;
        setDragging(null);
        setMouthHot(false);
        if (!drag) return;

        const overMouth =
          isNearMouth(upEvent.clientX, upEvent.clientY) || upEvent.clientY - drag.startY < -110;

        if (!drag.moved && !overMouth) {
          bumpInteraction();
          sounds.speakWord(item.label);
          return;
        }
        if (busyRef.current || !overMouth) return;
        attemptFeed(item, { x: upEvent.clientX, y: upEvent.clientY });
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [phase, bumpInteraction, isNearMouth, sounds, attemptFeed]
  );

  const beginMeal = useCallback(() => {
    clearTimers();
    // Cut any greeting that's still playing or mid-download — the child tapped
    // into the meal, so the intro is done whether or not it finished speaking.
    sounds.stop();
    const meal = makeSession(ROUNDS_PER_MEAL, trayLevelRef.current);
    roundHadMissRef.current = false;
    streakRef.current = 0;
    fedItemsRef.current = [];
    busyRef.current = false;
    roundIndexRef.current = 0;
    firstTryRef.current = 0;
    inFollowUpRef.current = false;
    missCountRef.current = 0;
    setRounds(meal);
    setRoundIndex(0);
    setFedCount(0);
    setFlashText(null);
    setInFollowUp(false);
    setMissCount(0);
    setPerfectWave(false);
    setStarsAwarded(0);
    setResultSnacks(0);
    setResultFirstTry(0);
    setMood("idle");
    setPhase("playing");
    // Warm the real Munchy voice for every line this meal will speak (round
    // prompts, follow-up asks, snack labels, praise/streak/halfway cheers, plus
    // the full/perfect lines) so they land in his character voice.
    const mealLabels = meal.flatMap((round) => (round.options || []).map((item) => item.label));
    const followUpPrompts = meal.map((round) => round.followUpPrompt).filter(Boolean);
    sounds.warm([
      ...meal.map((round) => round.prompt),
      ...followUpPrompts,
      ...mealLabels,
      ...PRAISE_LINES,
      ...STREAK_LINES,
      ...HALFWAY_LINES,
      FULL_LINE,
      PERFECT_LINE,
    ]);
    if (meal[0]) {
      warmRoundHints(meal[0]);
      // Munchy's very first snack request: wait for his real voice clip (like the
      // intro greeting does) instead of `speak`, which would blurt the robotic
      // synth understudy if the clip hasn't finished downloading within 500ms.
      schedule(() => sounds.speakReal(meal[0].prompt), 500);
    }
    bumpInteraction();
  }, [clearTimers, schedule, sounds, bumpInteraction, warmRoundHints]);

  const handleStart = useCallback(() => {
    setStarted(true);
    // This runs inside the child's tap, so unlock audio now: it lets the greeting
    // clip play in Munchy's real voice even though it finishes downloading a beat
    // later, instead of being blocked by autoplay rules into the synth understudy.
    sounds.unlock();
    [...Object.values(MONSTER_POSES), ...PANTRY.map((item) => item.asset)].forEach((src) => {
      const image = new window.Image();
      image.src = src;
    });
    // Warm Munchy's reactive lines up front so his praise/streak cheers play in
    // his real voice from the very first time they're heard. The intro itself uses
    // speakReal, which waits for the real Munchy clip before speaking, so the
    // greeting isn't blurted by the robotic synth understudy on the cold cache.
    sounds.warm([...PRAISE_LINES, ...STREAK_LINES]);
    sounds.speakReal(introLine);
  }, [sounds, introLine]);

  const handlePlayAgain = useCallback(() => {
    beginMeal();
  }, [beginMeal]);

  const handleExit = useCallback(() => {
    clearTimers();
    sounds.stop();
    navigate("/games");
  }, [clearTimers, sounds, navigate]);

  const compact = trayOptions.length >= 5;
  const bellyScale = 1 + fedCount * 0.012;
  // Munchy leans toward the snack the child is holding — a small "I want it!"
  // tilt that tracks the finger across the field (mirrors iOS `monsterLean`).
  const leanDeg = (() => {
    if (!dragging) return 0;
    const field = fieldRef.current?.getBoundingClientRect();
    if (!field || !field.width) return 0;
    const dx = (dragging.x - (field.left + field.width / 2)) / field.width;
    return Math.max(-6, Math.min(6, dx * 14));
  })();
  const isPerfectResult = resultFirstTry >= ROUNDS_PER_MEAL;

  const speakPrompt = useCallback(() => {
    if (currentAsk) sounds.speak(currentAsk);
  }, [currentAsk, sounds]);

  return (
    <main className="feed-monster" style={F}>
      <img className="fm-bg" src="/assets/games/feed-monster/fm_bg.png" alt="" aria-hidden="true" draggable="false" />
      <div className="fm-bg-scrim" aria-hidden="true" />

      <div className="fm-shell">
        <header className="fm-topbar">
          <BackToGamesButton onClick={handleExit} accent="#e8671d" />
          <div className="fm-title-wrap">
            <h1>Feed Munchy</h1>
            <p>{phase === "playing" ? `Snack ${Math.min(roundIndex + 1, ROUNDS_PER_MEAL)} of ${ROUNDS_PER_MEAL}` : "Listen • Drag • Feed"}</p>
          </div>
          <button
            type="button"
            className="fm-icon-button"
            onClick={sounds.toggleMute}
            aria-label={sounds.muted ? "Turn sound on" : "Mute sounds"}
            aria-pressed={sounds.muted}
          >
            <span className="fm-icon-glyph" aria-hidden="true"><SpeakerIcon size={22} muted={sounds.muted} /></span>
          </button>
        </header>

        {phase === "intro" && (
          <section className="fm-intro">
            <motion.img
              className="fm-intro-monster"
              src={MONSTER_POSES.idle}
              alt="Munchy the monster, waiting to be fed"
              animate={{ y: [-8, 8] }}
              transition={{ duration: 2.2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
              draggable="false"
            />
            <div className="fm-intro-card">
              <h2>Munchy the Monster</h2>
              <p>{introLine}</p>
              <button type="button" className="fm-primary-button" onClick={beginMeal}>
                Start the meal!
              </button>
            </div>
          </section>
        )}

        {(phase === "playing" || phase === "results") && (
          <section className="fm-board" ref={fieldRef} aria-label="Feed Munchy game">
            {currentRound && currentAsk && (
              <AnimatePresence mode="wait">
                <motion.button
                  type="button"
                  key={`${currentRound.id}-${inFollowUp}`}
                  className="fm-bubble"
                  onClick={speakPrompt}
                  initial={{ opacity: 0, scale: 0.8, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ type: "spring", stiffness: 220, damping: 20 }}
                  aria-label={`Munchy says: ${currentAsk}. Tap to hear again.`}
                >
                  {currentSwatch && (
                    <span className="fm-swatch" style={{ background: currentSwatch }} aria-hidden="true" />
                  )}
                  <span className="fm-bubble-text">{currentAsk}</span>
                  <span className="fm-bubble-speaker" aria-hidden="true"><SpeakerIcon size={20} /></span>
                </motion.button>
              </AnimatePresence>
            )}

            <div className="fm-stage">
              <motion.div
                className="fm-monster-bob"
                animate={{ y: [-6, 6] }}
                transition={{ duration: 2.2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
              >
                <motion.div className="fm-monster-shake" animate={monsterShake}>
                  <motion.div
                    ref={monsterRef}
                    className="fm-monster"
                    animate={{ scale: bellyScale * (mood === "chomp" ? 1.06 : mouthHot ? 1.04 : 1) }}
                    transition={{ type: "spring", stiffness: 260, damping: 18 }}
                  >
                   <motion.div
                     className="fm-monster-lean"
                     animate={{ rotate: leanDeg }}
                     transition={{ duration: 0.15, ease: "easeOut" }}
                   >
                    <div className={`fm-monster-chew ${mood === "chomp" ? "is-chewing" : ""}`}>
                    {Object.entries(MONSTER_POSES).map(([key, src]) => (
                      <img
                        key={key}
                        className={`fm-monster-pose ${mood === key ? "is-active" : ""}`}
                        src={src}
                        alt={key === "idle" ? "Munchy with his mouth open" : ""}
                        aria-hidden={key !== "idle"}
                        draggable="false"
                      />
                    ))}

                    <div className={`fm-mouth-zone ${mouthHot ? "is-hot" : ""}`} aria-hidden="true">
                      <span className="fm-mouth-glow" />
                      {dragging && <span className="fm-mouth-ring" />}
                    </div>

                    <AnimatePresence>
                      {mood === "happy" &&
                        HEARTS.map((heart) => (
                          <motion.span
                            key={`${sparkleKey}-${heart.id}`}
                            className="fm-heart"
                            aria-hidden="true"
                            initial={{ opacity: 0, y: 0, x: heart.x, scale: 0.5 }}
                            animate={{ opacity: [0, 1, 0], y: -120, scale: 1 }}
                            transition={{ duration: 1.3, delay: heart.delay, ease: "easeOut" }}
                          >
                            <HeartIcon size={30} />
                          </motion.span>
                        ))}
                    </AnimatePresence>
                    </div>
                   </motion.div>
                  </motion.div>
                </motion.div>
              </motion.div>

              <TummyMeter fed={fedCount} total={mealSnacks || ROUNDS_PER_MEAL} />
            </div>

            <motion.div
              className={`fm-tray ${compact ? "is-compact" : ""}`}
              animate={trayControls}
              aria-label="Snack tray"
            >
              {trayOptions.map((item) => {
                const glows =
                  missCount >= 2 && askSatisfied(currentRound, item, inFollowUp);
                return (
                  <button
                    type="button"
                    key={`${currentRound?.id || ""}-${inFollowUp}-${item.id}`}
                    className={`fm-tray-item ${glows ? "is-hinted" : ""}`}
                    onPointerDown={(event) => handlePointerDown(item, event)}
                    style={{ opacity: dragging?.item.id === item.id ? 0.22 : 1 }}
                    aria-label={item.label}
                  >
                    {glows && <span className="fm-hint-glow" aria-hidden="true" />}
                    <img src={item.asset} alt={item.label} draggable="false" />
                  </button>
                );
              })}
            </motion.div>
          </section>
        )}

        {!started && <StartModal onStart={handleStart} />}
      </div>

      {/* Lifted snack following the finger. */}
      {dragging && (
        <img
          className="fm-drag-ghost"
          src={dragging.item.asset}
          alt=""
          aria-hidden="true"
          draggable="false"
          style={{ left: dragging.x, top: dragging.y }}
        />
      )}

      {/* The correct snack flying into Munchy's mouth. */}
      <AnimatePresence>
        {flying && (
          <motion.img
            key="flying"
            className="fm-flying"
            src={flying.item.asset}
            alt=""
            aria-hidden="true"
            draggable="false"
            initial={{ left: flying.from.x, top: flying.from.y, scale: 1.15, opacity: 1 }}
            animate={{ left: flying.to.x, top: flying.to.y, scale: 0.15, opacity: 0.9 }}
            transition={{ duration: 0.28, ease: "easeIn" }}
            onAnimationComplete={() => swallow(flying.item)}
          />
        )}
      </AnimatePresence>

      {/* Sparkle burst at the mouth on a successful feed. */}
      <AnimatePresence>
        {sparkleKey > 0 && (
          <div
            key={sparkleKey}
            className="fm-sparkle-layer"
            style={{ left: sparklePoint.x, top: sparklePoint.y }}
            aria-hidden="true"
          >
            {SPARKLES.map((sparkle) => (
              <motion.span
                key={sparkle.id}
                className="fm-sparkle"
                initial={{ opacity: 1, x: 0, y: 0, scale: 0.4 }}
                animate={{ opacity: 0, x: sparkle.dx, y: sparkle.dy, scale: 1.1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Streak banner. */}
      <AnimatePresence>
        {flashText && (
          <motion.div
            className="fm-flash"
            initial={{ opacity: 0, scale: 0.6, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ type: "spring", stiffness: 260, damping: 16 }}
          >
            {flashText}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results overlay. */}
      <AnimatePresence>
        {phase === "results" && (
          <motion.div
            className="fm-result-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {CONFETTI_BITS.map((bit) => (
              <i key={bit.id} className="fm-confetti" style={{ left: bit.left, animationDelay: bit.delay, background: bit.color }} />
            ))}
            {perfectWave &&
              CONFETTI_BITS.map((bit) => (
                <i
                  key={`wave2-${bit.id}`}
                  className="fm-confetti fm-confetti--wave2"
                  style={{ left: bit.left, animationDelay: bit.delay, background: bit.color }}
                />
              ))}
            <motion.div
              className={`fm-result-card ${isPerfectResult ? "is-perfect" : ""}`}
              style={F}
              initial={{ scale: 0.8, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 18 }}
            >
              <div className="fm-result-hero" aria-hidden="true">
                <span className="fm-result-rays" />
                <span className="fm-result-disc" />
                <motion.img
                  className="fm-result-monster"
                  src={MONSTER_POSES.happy}
                  alt="Munchy, happy and full"
                  draggable="false"
                  animate={{ y: [-7, 7], rotate: [-2.5, 2.5] }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                />
              </div>

              <p className="fm-result-eyebrow">{isPerfectResult ? "WOW! Every one right!" : "Yummy yummy!"}</p>
              <h2>{isPerfectResult ? "PERFECT MEAL!" : "Munchy is full!"}</h2>
              <p className="fm-result-copy">
                {isPerfectResult
                  ? "Every single snack on the very first try. Munchy LOVES you!"
                  : `You fed Munchy all ${mealSnacks || resultSnacks} snacks — what great listening!`}
              </p>

              {/* Like iOS: no scores, streaks, or "best" numbers — just praise
                  and the reward. The animated gold badge when stars land, or a
                  gentle "come back tomorrow" when today's reward was claimed. */}
              {starsAwarded > 0 ? (
                <StarRewardBadge count={starsAwarded} />
              ) : (
                <div className="fm-result-comeback">
                  <span aria-hidden="true"><StarIcon size={14} /></span> Come back tomorrow for more stars!
                </div>
              )}

              <div className="fm-result-buttons">
                <button type="button" className="fm-primary-button" onClick={handlePlayAgain}>Feed him again!</button>
                <button type="button" className="fm-ghost-button" onClick={handleExit}>All done</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default FeedMonsterPage;
